import { useState } from 'react';
import { Button, Card, Group, Stack, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  launchAnalysis,
  patchAnalysis,
  useAnalysisAction,
  useAnalysisType,
  type AnalysisStaff,
  type Step,
} from '@/api/analyses';
import { errorMessage } from '@/api/errors';
import { useRunTypes } from '@/api/runs';
import { StaffBreadcrumbs } from '@/components/StaffBreadcrumbs';
import { StatusBadge } from '@/components/StatusBadge';
import { useFormat } from '@/components/useFormat';
import { ConfigForm, configOverrides, withDefaults, type ConfigValue, type JsonSchema } from '@/features/runs/ConfigForm';
import { InternalStateChip } from '@/features/staff/InternalStateChip';
import { brand } from '@/theme/theme';
import { AttachOutputsModal } from './AttachOutputsModal';
import { CancelAnalysisModal } from './CancelAnalysisModal';
import { DeliverModal } from './DeliverModal';
import { OutputsTable } from './OutputsTable';
import { RemoveAnalysisModal } from './RemoveAnalysisModal';
import { RerunModal } from './RerunModal';
import { RunChain } from './RunChain';
import { SlotBinder } from './SlotBinder';

type StepConfigs = Record<string, ConfigValue>;

/** Waits for the run types: the form seeds its state from the schema's defaults once, on mount. */
function StepConfig({ analysis, index, step }: { analysis: AnalysisStaff; index: number; step: Step }) {
  const { data: runTypes } = useRunTypes();
  if (!runTypes) return null;
  const rt = runTypes.find((r) => r.key === step.run_type);
  const schema = ((rt?.config_schema as JsonSchema | undefined) ?? { type: 'object' }) as JsonSchema;
  return <StepConfigForm analysis={analysis} index={index} step={step} schema={schema} />;
}

/** One step's config: the type's values as the base, this analysis's overrides on top; saving stores only the difference. */
function StepConfigForm({ analysis, index, step, schema }: { analysis: AnalysisStaff; index: number; step: Step; schema: JsonSchema }) {
  const { t } = useTranslation('analyses');
  const { t: tc } = useTranslation();
  const patch = useAnalysisAction(patchAnalysis);
  const stored = ((analysis.config ?? {}) as StepConfigs)[String(index)] ?? {};
  const base = withDefaults(schema, (step.config ?? {}) as ConfigValue);
  const [value, setValue] = useState<ConfigValue>(() => ({ ...base, ...stored }));
  const overrides = configOverrides(base, value);
  const dirty = JSON.stringify(overrides) !== JSON.stringify(configOverrides(base, { ...base, ...stored }));
  const save = async () => {
    try {
      await patch.mutateAsync({ id: analysis.public_id, args: [{ config: { ...(analysis.config ?? {}), [String(index)]: overrides } as Record<string, never> }] });
      notifications.show({ color: 'green', message: t('detail.configSaved') });
    } catch (err) {
      notifications.show({ color: 'red', message: errorMessage(err) });
    }
  };
  return (
    <Stack gap="xs">
      <Text fz={13} fw={600}>
        {t('chain.step', { n: index + 1 })} · {step.run_type}
      </Text>
      <ConfigForm schema={schema} value={value} onChange={setValue} />
      <Group justify="flex-end">
        <Button size="xs" loading={patch.isPending} disabled={!dirty} onClick={() => void save()}>
          {tc('common.save')}
        </Button>
      </Group>
    </Stack>
  );
}

export function AnalysisDetail({ analysis }: { analysis: AnalysisStaff }) {
  const { t, i18n } = useTranslation('analyses');
  const { dateTime } = useFormat();
  const { data: type } = useAnalysisType(analysis.type_key);
  const steps = (Array.isArray(type?.run_sequence) ? type!.run_sequence : []) as Step[];
  const launch = useAnalysisAction(launchAnalysis);
  const [rerun, setRerun] = useState<'failed' | 'review' | null>(null);
  const [deliverOpen, setDeliverOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [attachOpen, setAttachOpen] = useState(false);
  const navigate = useNavigate();
  const lang = i18n.language as 'cs' | 'en';
  const requiredUnbound = (analysis.inputs ?? []).some((i) => i.required && !i.data_object);

  const act = async (p: Promise<unknown>, done?: () => void) => {
    try {
      await p;
      done?.();
    } catch (err) {
      notifications.show({ color: 'red', message: errorMessage(err) });
    }
  };

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-start">
        <Stack gap={4}>
          <StaffBreadcrumbs projectId={analysis.project} current={analysis.name[lang] || analysis.name.cs} />
          <Group gap="sm">
            <Title order={1}>{analysis.name[lang] || analysis.name.cs}</Title>
            <InternalStateChip state={analysis.state ?? ''} />
            <StatusBadge status={analysis.client_status} scope="analysis" />
          </Group>
          <Text fz={13} c={brand.textMuted}>
            {analysis.type_name[lang] || analysis.type_name.cs} · {analysis.public_id} · {t('detail.created')} {dateTime(analysis.created_at)}
            {analysis.delivered_at ? ` · ${t('detail.delivered')} ${dateTime(analysis.delivered_at)}` : ''}
          </Text>
        </Stack>
        <Group gap="xs">
          {analysis.state === 'ready' && (
            <Button loading={launch.isPending} disabled={requiredUnbound} onClick={() => void act(launch.mutateAsync({ id: analysis.public_id, args: [] }))}>
              {t('actions.launch')}
            </Button>
          )}
          {analysis.state === 'planned' && (
            <Button disabled data-testid="launch-disabled">
              {t('actions.launch')}
            </Button>
          )}
          {analysis.state === 'failed' && <Button onClick={() => setRerun('failed')}>{t('actions.rerun')}</Button>}
          {analysis.state === 'review' && (
            <>
              <Button variant="default" onClick={() => setRerun('review')}>
                {t('actions.requestRerun')}
              </Button>
              <Button onClick={() => setDeliverOpen(true)}>{t('actions.deliver')}</Button>
            </>
          )}
          {analysis.removable && (
            <Button color="red" variant="light" onClick={() => setRemoveOpen(true)}>
              {t('actions.delete')}
            </Button>
          )}
          {analysis.state !== 'delivered' && analysis.state !== 'cancelled' && (
            <Button color="red" variant="light" onClick={() => setCancelOpen(true)}>
              {t('actions.cancel')}
            </Button>
          )}
        </Group>
      </Group>

      <Card>
        <Title order={3} mb="xs">
          {t('detail.slots')}
        </Title>
        <Stack gap={0}>
          {(analysis.inputs ?? []).map((i) => (
            <SlotBinder key={i.role} analysis={analysis} projectId={analysis.project} input={i} />
          ))}
        </Stack>
        {requiredUnbound && analysis.state === 'planned' && (
          <Text fz={13} c={brand.textMuted} mt="sm">
            {t('detail.bindHint')}
          </Text>
        )}
      </Card>

      {(analysis.state === 'planned' || analysis.state === 'ready') && steps.length > 0 && (
        <Card>
          <Title order={3} mb={4}>
            {t('detail.config')}
          </Title>
          <Text fz={13} c={brand.textMuted} mb="sm">
            {t('detail.configHint')}
          </Text>
          <Stack gap="lg">
            {steps.map((s, i) => (
              <StepConfig key={i} analysis={analysis} index={i} step={s} />
            ))}
          </Stack>
        </Card>
      )}

      <Title order={2}>{t('detail.chain')}</Title>
      <RunChain analysis={analysis} steps={steps} />

      <Group justify="space-between" align="center">
        <Title order={2}>{t('detail.outputs')}</Title>
        {analysis.state !== 'cancelled' && (
          <Button variant="default" onClick={() => setAttachOpen(true)}>
            {t('attach.button')}
          </Button>
        )}
      </Group>
      <OutputsTable outputs={analysis.all_outputs ?? []} staff />

      {rerun && <RerunModal analysis={analysis} steps={steps} mode={rerun} onClose={() => setRerun(null)} />}
      <DeliverModal analysis={analysis} opened={deliverOpen} onClose={() => setDeliverOpen(false)} />
      <CancelAnalysisModal analysis={analysis} opened={cancelOpen} onClose={() => setCancelOpen(false)} />
      {attachOpen && <AttachOutputsModal analysis={analysis} opened onClose={() => setAttachOpen(false)} />}
      {analysis.removable && (
        <RemoveAnalysisModal
          analysis={analysis}
          opened={removeOpen}
          onClose={() => setRemoveOpen(false)}
          onRemoved={() => void navigate({ to: '/staff/projects/$projectId', params: { projectId: analysis.project } })}
        />
      )}
    </Stack>
  );
}
